import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";
import { 
  Navigation, MapPin, Clock, Car, Truck, Accessibility, Plus, X, 
  Bell, LogOut, CalendarIcon, Phone, User, ChevronRight, MessageSquare,
  Send, CheckCircle, XCircle, Loader2, Share2, Copy, ExternalLink,
  Briefcase, ShoppingCart, Church, Package, RotateCcw, Timer, DollarSign,
  Banknote
} from "lucide-react";
import NotificationPanel from "@/components/NotificationPanel";
import SMSLogPanel from "@/components/SMSLogPanel";
import RideProgressTimeline from "@/components/RideProgressTimeline";
import LiveFleetMap from "@/components/LiveFleetMap";
import WaitTimer from "@/components/WaitTimer";
import CalendarView from "@/components/calendar/CalendarView";
import RideDetailDialog from "@/components/calendar/RideDetailDialog";
import { phaseLabel, etaTargetLabel } from "@/lib/tripPhase";
import PrivateRideWizard from "@/components/PrivateRideWizard";

export default function ClientDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [calendarRideId, setCalendarRideId] = useState(null);
  const [rides, setRides] = useState([]);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSMSLog, setShowSMSLog] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [shareModal, setShareModal] = useState({ open: false, ride: null, shareUrl: null });
  const [trackingRide, setTrackingRide] = useState(null);
  const [clientLocation, setClientLocation] = useState(null);

  // Get client's location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setClientLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => console.log("Location access denied")
      );
    }
  }, []);

  const [formData, setFormData] = useState({
    client_name: user?.name || "",
    client_phone: user?.phone || "",
    pickup_locations: [{ address: "", latitude: 0, longitude: 0, order: 0 }],
    dropoff_address: "",
    dropoff_latitude: 0,
    dropoff_longitude: 0,
    pickup_date: "",
    pickup_time: "",
    vehicle_type: "car",
    special_requests: "",
    pin_code: "",
    mobility_aids: [],
    assistance_needs: [],
    preferred_driver: "",
    ride_sharing_preference: "alone", // "alone" or "shared"
    // Ride type and service type - empty by default to show selector first
    ride_type: "", // "nemt" or "private" - empty means show selector
    service_type: "", // one_way, round_trip
    // Private ride specific fields
    trip_purpose: "", // grocery, work, church, errands_multiple
    has_multiple_stops: false,
    stops: [], // Array of { address, wait, wait_minutes }
    wait_and_return: false,
    wait_time: 0, // minutes for wait & return
    return_pickup_time: "",
    return_time_tbc: false,
    estimated_miles: 0, // for price estimation
    fare_estimate: 0, // calculated price
    payment_method: "", // card, cashapp, chime, applepay, cash
    // Private ride assistance fields
    needs_entry_assistance: null, // Does client need help getting in/out of vehicle?
    mobility_aid: null, // walker, cane, other, none
    mobility_aid_other: "", // Description if "other" selected
    accessibility_need: null, // hearing_impaired, visually_impaired, other, none
    accessibility_other: "", // Description if "other" selected
    has_service_animal: null // Does client have a service animal?
  });

  useEffect(() => {
    fetchRides();
    fetchNotifications();
    const interval = setInterval(() => {
      fetchRides();
      fetchNotifications();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchRides = async () => {
    try {
      const response = await apiClient.get("/rides");
      setRides(response.data);
    } catch (error) {
      console.error("Failed to fetch rides:", error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await apiClient.get("/notifications");
      const unread = response.data.filter(n => !n.is_read).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  };

  // Share ETA with family/caregivers
  const handleShareETA = async (ride) => {
    try {
      const response = await apiClient.post(`/rides/${ride.id}/share-eta`);
      const fullUrl = `${window.location.origin}${response.data.share_url}`;
      setShareModal({ 
        open: true, 
        ride, 
        shareUrl: fullUrl,
        rideInfo: response.data.ride_info
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create share link");
    }
  };

  const copyShareLink = async () => {
    if (shareModal.shareUrl) {
      await navigator.clipboard.writeText(shareModal.shareUrl);
      toast.success("Link copied to clipboard!");
    }
  };

  const handleAddPickup = () => {
    setFormData({
      ...formData,
      pickup_locations: [
        ...formData.pickup_locations,
        { address: "", latitude: 0, longitude: 0, order: formData.pickup_locations.length }
      ]
    });
  };

  const handleRemovePickup = (index) => {
    if (formData.pickup_locations.length > 1) {
      const updated = formData.pickup_locations.filter((_, i) => i !== index);
      setFormData({ ...formData, pickup_locations: updated });
    }
  };

  const handlePickupChange = (index, value) => {
    const updated = [...formData.pickup_locations];
    updated[index] = { ...updated[index], address: value };
    setFormData({ ...formData, pickup_locations: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.pin_code.length !== 4 || !/^\d{4}$/.test(formData.pin_code)) {
      toast.error("PIN must be exactly 4 digits");
      return;
    }

    setIsLoading(true);
    try {
      await apiClient.post("/rides", formData);
      toast.success("Ride request submitted! Awaiting dispatch approval.");
      setShowRequestForm(false);
      fetchRides();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client_name: user?.name || "",
      client_phone: user?.phone || "",
      pickup_locations: [{ address: "", latitude: 0, longitude: 0, order: 0 }],
      dropoff_address: "",
      dropoff_latitude: 0,
      dropoff_longitude: 0,
      pickup_date: "",
      pickup_time: "",
      vehicle_type: "car",
      special_requests: "",
      pin_code: "",
      mobility_aids: [],
      assistance_needs: [],
      preferred_driver: "",
      ride_sharing_preference: "alone",
      ride_type: "", // Empty to show selector first
      service_type: "",
      trip_purpose: "",
      has_multiple_stops: false,
      stops: [],
      wait_and_return: false,
      wait_time: 0,
      return_pickup_time: "",
      return_time_tbc: false,
      estimated_miles: 0,
      fare_estimate: 0,
      payment_method: "",
      // Private ride assistance fields
      needs_entry_assistance: null,
      mobility_aid: null,
      mobility_aid_other: "",
      accessibility_need: null,
      accessibility_other: "",
      has_service_animal: null
    });
    setSelectedDate(null);
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      requested: { color: "bg-orange-100 text-orange-800", label: "Pending Approval", icon: Loader2 },
      approved: { color: "bg-blue-100 text-blue-800", label: "Approved", icon: CheckCircle },
      rejected: { color: "bg-red-100 text-red-800", label: "Declined", icon: XCircle },
      scheduled: { color: "bg-purple-100 text-purple-800", label: "Scheduled", icon: CheckCircle },
      in_progress: { color: "bg-indigo-100 text-indigo-800", label: "Driver En Route", icon: Navigation },
      arrived: { color: "bg-lime-100 text-lime-800", label: "Driver Arrived", icon: MapPin },
      completed: { color: "bg-green-100 text-green-800", label: "Completed", icon: CheckCircle }
    };
    return statusMap[status] || { color: "bg-slate-100 text-slate-800", label: status, icon: Clock };
  };

  const vehicleIcons = {
    car: Car,
    van: Truck,
    handicap_van: Accessibility
  };

  const pendingRequests = rides.filter(r => ["requested", "approved"].includes(r.status));
  const scheduledRides = rides.filter(r => ["scheduled", "in_progress", "arrived"].includes(r.status));
  const completedRides = rides.filter(r => ["completed", "rejected"].includes(r.status));

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 pb-20 md:pb-8">
      {/* Decorative Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-orange-200/30 to-pink-200/30 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-teal-200/30 to-cyan-200/30 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
      </div>
      
      {/* Header */}
      <header className="bg-gradient-to-r from-orange-500 via-rose-500 to-pink-500 text-white sticky top-0 z-40 safe-area-top shadow-lg shadow-rose-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <Navigation className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
              <span className="text-xl md:text-2xl font-heading font-black text-white drop-shadow-sm">There You Are</span>
            </div>
            
            <div className="flex items-center gap-3 md:gap-5">
              <Button
                variant="ghost"
                size="icon"
                className="relative w-12 h-12 text-white hover:text-white hover:bg-white/20"
                onClick={() => setShowSMSLog(true)}
                data-testid="sms-log-button"
              >
                <MessageSquare className="w-7 h-7" strokeWidth={2.5} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="relative w-12 h-12 text-white hover:text-white hover:bg-white/20"
                onClick={() => setShowNotifications(true)}
                data-testid="notifications-button"
              >
                <Bell className="w-7 h-7" strokeWidth={2.5} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-6 h-6 bg-yellow-400 text-orange-900 text-sm rounded-full flex items-center justify-center font-bold">
                    {unreadCount}
                  </span>
                )}
              </Button>
              <div className="hidden md:flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-xl">
                <User className="w-5 h-5 text-white" strokeWidth={2.5} />
                <span className="text-base font-bold text-white">{user?.name}</span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="w-12 h-12 hover:bg-white/20"
                onClick={logout}
                data-testid="logout-button"
              >
                <LogOut className="w-7 h-7 text-white" strokeWidth={2.5} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10 relative z-10 text-lg">
        {/* Welcome Section */}
        <div className="mb-8 md:mb-10">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-slate-800 mb-2 md:mb-3">
            Welcome back, {user?.name?.split(' ')[0]}! 👋
          </h1>
          <p className="text-slate-600 text-lg md:text-xl">Ready to schedule your next ride? We're here to help!</p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-10">
          {/* REQUEST A RIDE - Main CTA */}
          <Card 
            className="lg:col-span-1 bg-gradient-to-br from-orange-500 via-rose-500 to-pink-600 text-white border-0 cursor-pointer hover:from-orange-400 hover:via-rose-400 hover:to-pink-500 transition-all shadow-xl shadow-rose-500/30 hover:shadow-2xl hover:shadow-rose-500/40 hover:scale-[1.02] active:scale-[0.98]"
            onClick={() => setShowRequestForm(true)}
            data-testid="request-ride-card"
          >
            <CardContent className="p-8 md:p-10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-rose-100 text-sm uppercase tracking-wider font-semibold mb-2">Need a ride?</p>
                  <h3 className="text-3xl md:text-4xl font-heading font-black mb-3">Request a Ride</h3>
                  <p className="text-rose-100 text-base md:text-lg">Tap here to schedule your transportation</p>
                </div>
                <div className="w-20 h-20 md:w-24 md:h-24 bg-white rounded-2xl flex items-center justify-center shadow-lg animate-pulse">
                  <Send className="w-10 h-10 md:w-12 md:h-12 text-rose-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="lg:col-span-2 border-0 bg-white/80 backdrop-blur-sm shadow-lg">
            <CardContent className="p-6 md:p-8">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-heading font-bold text-slate-800 text-xl">My Requests</h3>
                <Badge className="bg-orange-100 text-orange-700 border-0 text-base px-3 py-1">{pendingRequests.length} pending</Badge>
              </div>
              {pendingRequests.length === 0 ? (
                <p className="text-slate-500 text-base">No pending requests</p>
              ) : (
                <div className="space-y-4">
                  {pendingRequests.slice(0, 2).map((ride) => {
                    const statusInfo = getStatusInfo(ride.status);
                    const VehicleIcon = vehicleIcons[ride.vehicle_type] || Car;
                    return (
                      <div 
                        key={ride.id}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => navigate(`/ride/${ride.id}`)}
                        data-testid={`ride-item-${ride.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
                            <VehicleIcon className="w-6 h-6 text-indigo-900" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 text-base">{ride.pickup_locations[0]?.address}</p>
                            <p className="text-slate-500 text-sm">{ride.pickup_date} at {ride.pickup_time}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={`${statusInfo.color} text-sm`}>{statusInfo.label}</Badge>
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Scheduled Rides - Live Tracking Always Visible */}
        {scheduledRides.length > 0 && (
          <Card className="border-0 mb-8 bg-gradient-to-br from-teal-500 via-cyan-500 to-blue-500 shadow-xl shadow-cyan-500/20" data-testid="scheduled-rides-section">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-xl flex items-center gap-2 text-white">
                <Navigation className="w-6 h-6" />
                Your Active Rides
                <Badge className="bg-white/20 text-white animate-pulse">LIVE TRACKING</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {scheduledRides.map((ride) => {
                  const statusInfo = getStatusInfo(ride.status);
                  const VehicleIcon = vehicleIcons[ride.vehicle_type] || Car;
                  const isTracking = trackingRide === ride.id;
                  const isActiveRide = ride.status === "in_progress" || ride.status === "arrived";
                  
                  return (
                    <div key={ride.id} className="space-y-4" data-testid={`scheduled-ride-${ride.id}`}>
                      {/* Ride Info Card */}
                      <div className="p-4 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg">
                              <VehicleIcon className="w-6 h-6 text-teal-600" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <MapPin className="w-4 h-4 text-yellow-300" />
                                <span className="font-medium text-white">{ride.pickup_locations[0]?.address}</span>
                              </div>
                              <p className="text-teal-100 text-sm">{ride.pickup_date} at {ride.pickup_time}</p>
                              {ride.driver_name && (
                                <p className="text-white text-sm font-semibold mt-1">Driver: {ride.driver_name}</p>
                              )}
                            </div>
                          </div>
                          <div className="text-right flex flex-col items-end gap-2">
                            <Badge className="bg-yellow-400 text-yellow-900 border-0" data-testid={`ride-phase-${ride.id}`}>{ride.trip_phase ? phaseLabel(ride) : statusInfo.label}</Badge>
                            {ride.eta_minutes && ride.status === "in_progress" && (
                              <p className="text-white font-bold text-lg" data-testid={`client-eta-${ride.id}`}>ETA {etaTargetLabel(ride)}: {ride.eta_minutes} min</p>
                            )}
                            {/* Share ETA Button */}
                            {["scheduled", "in_progress", "arrived"].includes(ride.status) && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-white/50 text-white hover:bg-white/20"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShareETA(ride);
                                }}
                                data-testid={`share-eta-${ride.id}`}
                              >
                                <Share2 className="w-3 h-3 mr-1" /> Share ETA
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* ALWAYS VISIBLE: Live Map and Compact Timeline for active rides */}
                      {isActiveRide && (
                        <div className="bg-white rounded-2xl overflow-hidden shadow-lg" data-testid={`live-tracking-${ride.id}`}>
                          {/* Compact Timeline - Always Visible */}
                          <div className="p-4 border-b border-slate-100">
                            <RideProgressTimeline 
                              ride={ride} 
                              variant="light" 
                              compact={true}
                              showDriver={false}
                              showETA={true}
                            />
                          </div>
                          
                          {/* Live Map - Always Visible for in_progress/arrived */}
                          <div className="relative">
                            <div className="absolute top-3 left-3 z-10 bg-white/90 backdrop-blur px-3 py-1.5 rounded-lg shadow-md">
                              <p className="text-xs font-semibold text-cyan-600 flex items-center gap-1">
                                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                                Live Driver Location
                              </p>
                            </div>
                            <LiveFleetMap 
                              mode="client"
                              currentRide={ride}
                              clientLocation={clientLocation}
                              height="300px"
                              variant="light"
                              showControls={true}
                            />
                          </div>
                          {ride.wait_segments?.length > 0 && (
                            <div className="p-4 border-t border-slate-100">
                              <WaitTimer ride={ride} variant="light" />
                            </div>
                          )}
                          
                          {/* Expand for more details button */}
                          <button
                            onClick={() => setTrackingRide(isTracking ? null : ride.id)}
                            className="w-full py-3 text-sm font-semibold text-cyan-600 hover:bg-cyan-50 transition-colors flex items-center justify-center gap-2"
                          >
                            {isTracking ? "Hide Details" : "Show More Details"}
                            <ChevronRight className={`w-4 h-4 transition-transform ${isTracking ? 'rotate-90' : ''}`} />
                          </button>
                        </div>
                      )}
                      
                      {/* For scheduled (not yet in_progress) - show compact card with expand option */}
                      {!isActiveRide && (
                        <div 
                          className="bg-white/10 backdrop-blur rounded-xl p-3 cursor-pointer hover:bg-white/20 transition-all"
                          onClick={() => setTrackingRide(isTracking ? null : ride.id)}
                        >
                          <div className="flex items-center justify-center text-white/80 text-sm gap-2">
                            <Clock className="w-4 h-4" />
                            <span>Waiting for driver to start trip</span>
                            <span className="text-xs">(Tap for details)</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Expanded Tracking View - Full Details */}
                      {isTracking && (
                        <div className="bg-white rounded-2xl p-4 shadow-lg space-y-4 animate-in slide-in-from-top-2" data-testid={`tracking-view-${ride.id}`}>
                          {/* Full Progress Timeline */}
                          <RideProgressTimeline 
                            ride={ride} 
                            variant="light" 
                            compact={false}
                            showDriver={true}
                            showETA={true}
                          />
                          
                          {/* Expanded Map for non-active rides */}
                          {!isActiveRide && (
                            <div className="mt-4">
                              <h4 className="text-sm font-semibold text-slate-600 mb-2 flex items-center gap-2">
                                <Navigation className="w-4 h-4" />
                                Route Preview
                              </h4>
                              <LiveFleetMap 
                                mode="client"
                                currentRide={ride}
                                clientLocation={clientLocation}
                                height="250px"
                                variant="light"
                                showControls={true}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* My Upcoming Rides calendar (approved rides only) */}
        <div className="mb-8" data-testid="client-calendar-section">
          <h2 className="text-xl font-heading font-bold text-slate-900 mb-3">My Upcoming Rides</h2>
          <CalendarView variant="light" defaultView="list" audience="client" onOpenRide={setCalendarRideId} testId="client-calendar" />
        </div>
        <RideDetailDialog rideId={calendarRideId} onClose={() => setCalendarRideId(null)} mode="client" />

        {/* All Requests History */}
        <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-lg" data-testid="all-rides-section">
          <CardHeader>
            <CardTitle className="font-heading text-slate-800">Request History</CardTitle>
          </CardHeader>
          <CardContent>
            {rides.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-rose-500 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <Send className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">No requests yet</h3>
                <p className="text-slate-500 mb-4">Submit your first ride request to dispatch</p>
                <Button 
                  onClick={() => setShowRequestForm(true)}
                  className="bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-400 hover:to-rose-400 text-white border-0"
                  data-testid="submit-first-request-button"
                >
                  Request a Ride
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {rides.map((ride) => {
                  const statusInfo = getStatusInfo(ride.status);
                  const VehicleIcon = vehicleIcons[ride.vehicle_type] || Car;
                  const StatusIcon = statusInfo.icon;
                  return (
                    <div 
                      key={ride.id}
                      className="flex items-center justify-between p-4 border border-slate-200 rounded-xl hover:shadow-md hover:border-orange-200 transition-all cursor-pointer bg-white"
                      onClick={() => navigate(`/ride/${ride.id}`)}
                      data-testid={`ride-card-${ride.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-rose-100 rounded-xl flex items-center justify-center">
                          <VehicleIcon className="w-6 h-6 text-rose-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <MapPin className="w-4 h-4 text-teal-500" />
                            <span className="font-medium text-slate-800">{ride.pickup_locations[0]?.address}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-rose-500" />
                            <span className="text-slate-600 text-sm">{ride.dropoff_address}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={statusInfo.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusInfo.label}
                        </Badge>
                        <p className="text-slate-500 text-sm mt-1">{ride.pickup_date}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Request Form Modal */}
      {showRequestForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto border-0 shadow-2xl bg-white" data-testid="request-form-modal">
            <CardHeader className="sticky top-0 bg-gradient-to-r from-orange-500 via-rose-500 to-pink-500 z-10 text-white rounded-t-lg">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-xl text-white">Request a Ride</CardTitle>
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={() => { setShowRequestForm(false); resetForm(); }}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <p className="text-rose-100 text-sm">
                {formData.ride_type === "private" ? "Book your personal transportation" : "Fill out your information for dispatch approval"}
              </p>
            </CardHeader>
            <CardContent className="p-6 min-h-[400px]">
              {/* Ride Type Selection and Forms */}
              <div className="space-y-6">
                {/* RIDE TYPE SELECTOR - Show when no type selected or still selecting */}
                {(!formData.ride_type || formData.ride_type === "private") && (
                  <div className="space-y-3">
                    <Label className="text-base font-semibold">What type of ride do you need?</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, ride_type: "nemt", service_type: "one_way" })}
                        className={`p-4 rounded-xl border-2 transition-all text-left ${
                          formData.ride_type === "nemt"
                            ? "border-blue-500 bg-blue-50 shadow-md"
                            : "border-slate-200 hover:border-blue-200 bg-white"
                        }`}
                        data-testid="ride-type-nemt"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            formData.ride_type === "nemt" ? "bg-blue-500 text-white" : "bg-slate-100 text-slate-500"
                          }`}>
                            <Accessibility className="w-6 h-6" />
                          </div>
                          <div>
                            <p className={`font-bold ${formData.ride_type === "nemt" ? "text-blue-700" : "text-slate-700"}`}>
                              NEMT
                            </p>
                            <p className="text-xs text-slate-500">Medical Transportation</p>
                          </div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, ride_type: "private", service_type: "" })}
                        className={`p-4 rounded-xl border-2 transition-all text-left ${
                          formData.ride_type === "private"
                            ? "border-orange-500 bg-orange-50 shadow-md"
                            : "border-slate-200 hover:border-orange-200 bg-white"
                        }`}
                        data-testid="ride-type-private"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            formData.ride_type === "private" ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-500"
                          }`}>
                            <Car className="w-6 h-6" />
                          </div>
                          <div>
                            <p className={`font-bold ${formData.ride_type === "private" ? "text-orange-700" : "text-slate-700"}`}>
                              Private Ride
                            </p>
                            <p className="text-xs text-slate-500">Personal Transportation</p>
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                )}

                {/* Show Private Ride Wizard if private ride selected */}
                {formData.ride_type === "private" && (
                  <div className="pt-4 border-t">
                    <PrivateRideWizard
                      formData={formData}
                      setFormData={setFormData}
                      onSubmit={handleSubmit}
                      onCancel={() => { setShowRequestForm(false); resetForm(); }}
                      isLoading={isLoading}
                      selectedDate={selectedDate}
                      setSelectedDate={setSelectedDate}
                    />
                  </div>
                  )}

                  {/* NEMT Form - Show full form for NEMT rides */}
                  {formData.ride_type === "nemt" && (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Personal Info */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Your Name</Label>
                          <Input
                            value={formData.client_name}
                            onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                            required
                            data-testid="request-name-input"
                            className="h-12"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Phone Number</Label>
                          <Input
                            value={formData.client_phone}
                            onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                            required
                            data-testid="request-phone-input"
                            className="h-12"
                          />
                        </div>
                      </div>

                {/* Pickup Locations */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Pickup Location(s)</Label>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={handleAddPickup}
                      data-testid="add-pickup-button"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Stop
                    </Button>
                  </div>
                  {formData.pickup_locations.map((loc, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-blue-600">{index + 1}</span>
                      </div>
                      <Input
                        placeholder={`Pickup address ${index + 1}`}
                        value={loc.address}
                        onChange={(e) => handlePickupChange(index, e.target.value)}
                        required
                        className="h-12 flex-1"
                        data-testid={`pickup-address-${index}`}
                      />
                      {formData.pickup_locations.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemovePickup(index)}
                        >
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Dropoff */}
                <div className="space-y-2">
                  <Label>Dropoff Address</Label>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-3 h-3 text-red-600" />
                    </div>
                    <Input
                      placeholder="Final destination"
                      value={formData.dropoff_address}
                      onChange={(e) => setFormData({ ...formData, dropoff_address: e.target.value })}
                      required
                      className="h-12 flex-1"
                      data-testid="dropoff-address-input"
                    />
                  </div>
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Pickup Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full h-12 justify-start text-left font-normal"
                          data-testid="pickup-date-button"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {selectedDate ? format(selectedDate, "PPP") : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={(date) => {
                            setSelectedDate(date);
                            setFormData({ ...formData, pickup_date: date ? format(date, "yyyy-MM-dd") : "" });
                          }}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>Pickup Time</Label>
                    <Input
                      type="time"
                      value={formData.pickup_time}
                      onChange={(e) => setFormData({ ...formData, pickup_time: e.target.value })}
                      required
                      className="h-12"
                      data-testid="pickup-time-input"
                    />
                  </div>
                </div>

                {/* Vehicle Type */}
                <div className="space-y-2">
                  <Label>Vehicle Type</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: "car", label: "Car", icon: Car },
                      { value: "van", label: "Van", icon: Truck },
                      { value: "handicap_van", label: "Handicap Van", icon: Accessibility }
                    ].map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, vehicle_type: type.value })}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          formData.vehicle_type === type.value
                            ? "border-rose-500 bg-rose-50"
                            : "border-slate-200 hover:border-rose-200"
                        }`}
                        data-testid={`vehicle-${type.value}`}
                      >
                        <type.icon className={`w-6 h-6 mx-auto mb-2 ${
                          formData.vehicle_type === type.value ? "text-rose-600" : "text-slate-400"
                        }`} />
                        <span className={`text-sm font-medium ${
                          formData.vehicle_type === type.value ? "text-rose-600" : "text-slate-600"
                        }`}>{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Mobility Aids */}
                <div className="space-y-3">
                  <Label>Mobility Aids (Select all that apply)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { value: "rolling_walker", label: "Rolling Walker" },
                      { value: "cane", label: "Cane" },
                      { value: "crutches", label: "Crutches" },
                      { value: "wheelchair", label: "Wheelchair" }
                    ].map((aid) => (
                      <button
                        key={aid.value}
                        type="button"
                        onClick={() => {
                          const current = formData.mobility_aids || [];
                          const updated = current.includes(aid.value)
                            ? current.filter(a => a !== aid.value)
                            : [...current, aid.value];
                          setFormData({ ...formData, mobility_aids: updated });
                        }}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          (formData.mobility_aids || []).includes(aid.value)
                            ? "border-teal-500 bg-teal-50 text-teal-700"
                            : "border-slate-200 hover:border-teal-200 text-slate-600"
                        }`}
                        data-testid={`mobility-${aid.value}`}
                      >
                        <span className="text-sm font-medium">{aid.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Assistance Needs */}
                <div className="space-y-3">
                  <Label>Assistance Needs (Select all that apply)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { value: "step_stool", label: "Step Stool Needed" },
                      { value: "entry_exit_help", label: "Help In/Out of Vehicle" },
                      { value: "blind", label: "Visually Impaired" },
                      { value: "deaf", label: "Hearing Impaired" },
                      { value: "oxygen", label: "Oxygen Tank" },
                      { value: "service_animal", label: "Service Animal" }
                    ].map((need) => (
                      <button
                        key={need.value}
                        type="button"
                        onClick={() => {
                          const current = formData.assistance_needs || [];
                          const updated = current.includes(need.value)
                            ? current.filter(n => n !== need.value)
                            : [...current, need.value];
                          setFormData({ ...formData, assistance_needs: updated });
                        }}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          (formData.assistance_needs || []).includes(need.value)
                            ? "border-orange-500 bg-orange-50 text-orange-700"
                            : "border-slate-200 hover:border-orange-200 text-slate-600"
                        }`}
                        data-testid={`assistance-${need.value}`}
                      >
                        <span className="text-sm font-medium">{need.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Preferred Driver */}
                <div className="space-y-2">
                  <Label>Preferred Driver (Optional)</Label>
                  <Input
                    placeholder="Enter driver name if you have a preference"
                    value={formData.preferred_driver}
                    onChange={(e) => setFormData({ ...formData, preferred_driver: e.target.value })}
                    className="h-12"
                    data-testid="preferred-driver-input"
                  />
                  <p className="text-xs text-slate-500">If available, dispatch will try to assign your preferred driver</p>
                </div>

                {/* PIN Code */}
                <div className="space-y-2">
                  <Label>4-Digit PIN Code</Label>
                  <p className="text-xs text-slate-500 mb-2">Create a PIN for driver verification upon arrival</p>
                  <Input
                    type="text"
                    maxLength={4}
                    placeholder="1234"
                    value={formData.pin_code}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      setFormData({ ...formData, pin_code: val });
                    }}
                    required
                    className="h-14 text-center text-2xl font-mono tracking-[0.5em] border-2 focus:border-rose-400"
                    data-testid="pin-code-input"
                  />
                </div>

                {/* Ride Sharing Preference */}
                <div className="space-y-3 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-200">
                  <Label className="text-base font-semibold text-slate-700 flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-500" />
                    Ride Sharing Preference
                  </Label>
                  <p className="text-sm text-slate-500">Would you mind sharing your ride with other clients going in a similar direction?</p>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, ride_sharing_preference: "alone" })}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        formData.ride_sharing_preference === "alone"
                          ? "border-rose-500 bg-rose-50 shadow-md"
                          : "border-slate-200 hover:border-slate-300 bg-white"
                      }`}
                      data-testid="ride-alone-btn"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          formData.ride_sharing_preference === "alone" ? "bg-rose-500 text-white" : "bg-slate-100 text-slate-500"
                        }`}>
                          <User className="w-5 h-5" />
                        </div>
                        <div>
                          <p className={`font-semibold ${formData.ride_sharing_preference === "alone" ? "text-rose-700" : "text-slate-700"}`}>
                            Ride Alone
                          </p>
                          <p className="text-xs text-slate-500">Private ride, just me</p>
                        </div>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, ride_sharing_preference: "shared" })}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        formData.ride_sharing_preference === "shared"
                          ? "border-green-500 bg-green-50 shadow-md"
                          : "border-slate-200 hover:border-slate-300 bg-white"
                      }`}
                      data-testid="ride-shared-btn"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          formData.ride_sharing_preference === "shared" ? "bg-green-500 text-white" : "bg-slate-100 text-slate-500"
                        }`}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <div>
                          <p className={`font-semibold ${formData.ride_sharing_preference === "shared" ? "text-green-700" : "text-slate-700"}`}>
                            OK to Share
                          </p>
                          <p className="text-xs text-slate-500">Don't mind others</p>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Special Requests */}
                <div className="space-y-2">
                  <Label>Special Requests (Optional)</Label>
                  <Textarea
                    placeholder="Any special requirements or notes for dispatch..."
                    value={formData.special_requests}
                    onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
                    rows={3}
                    data-testid="special-requests-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-400 hover:to-cyan-400 font-semibold text-white border-0 shadow-lg shadow-blue-500/30"
                  disabled={isLoading}
                  data-testid="submit-request-button"
                >
                  {isLoading ? "Submitting..." : "Submit NEMT Request"}
                </Button>
              </form>
            )}
          </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Notifications Panel */}
      <NotificationPanel 
        isOpen={showNotifications} 
        onClose={() => setShowNotifications(false)} 
      />

      {/* SMS Log Panel */}
      <SMSLogPanel 
        isOpen={showSMSLog} 
        onClose={() => setShowSMSLog(false)} 
      />

      {/* Share ETA Modal */}
      {shareModal.open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md bg-white">
            <CardHeader className="border-b bg-gradient-to-r from-teal-500 to-cyan-500 rounded-t-lg text-white">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading flex items-center gap-2 text-white">
                  <Share2 className="w-5 h-5" />
                  Share Ride Tracking
                </CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/20"
                  onClick={() => setShareModal({ open: false, ride: null, shareUrl: null })}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <p className="text-slate-600">
                Share this link with family or caregivers so they can track your ride in real-time.
              </p>
              
              {/* Share Link */}
              <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-lg">
                <Input
                  value={shareModal.shareUrl || ""}
                  readOnly
                  className="border-0 bg-transparent text-sm"
                />
                <Button
                  size="sm"
                  onClick={copyShareLink}
                  className="bg-cyan-600 hover:bg-cyan-700"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>

              {/* Ride Info Preview */}
              {shareModal.rideInfo && (
                <div className="p-3 bg-indigo-50 rounded-lg text-sm">
                  <p className="font-semibold text-indigo-900 mb-1">Ride Details</p>
                  <p className="text-slate-600">
                    <MapPin className="w-3 h-3 inline mr-1" />
                    To: {shareModal.rideInfo.dropoff_address}
                  </p>
                  <p className="text-slate-600">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {shareModal.rideInfo.pickup_date} at {shareModal.rideInfo.pickup_time}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    const text = `Track my ride: ${shareModal.shareUrl}`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                  }}
                >
                  Share via WhatsApp
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    const text = `Track my ride to my appointment: ${shareModal.shareUrl}`;
                    window.open(`sms:?body=${encodeURIComponent(text)}`, '_blank');
                  }}
                >
                  Share via SMS
                </Button>
              </div>

              <p className="text-xs text-slate-500 text-center">
                Link expires when the ride is completed
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-gradient-to-r from-orange-500 via-rose-500 to-pink-500 safe-area-bottom z-40 shadow-lg shadow-rose-500/30">
        <div className="flex items-center justify-around py-2">
          <button 
            className="flex flex-col items-center gap-1 p-2 text-white"
            data-testid="nav-home"
          >
            <Navigation className="w-5 h-5" />
            <span className="text-xs font-medium">Home</span>
          </button>
          <button 
            onClick={() => setShowRequestForm(true)}
            className="flex flex-col items-center gap-1 p-2 -mt-6"
            data-testid="nav-request"
          >
            <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-xl border-4 border-rose-300">
              <Send className="w-6 h-6 text-rose-500" />
            </div>
          </button>
          <button 
            onClick={() => setShowNotifications(true)}
            className="flex flex-col items-center gap-1 p-2 text-white relative"
            data-testid="nav-alerts"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-2 w-4 h-4 bg-yellow-400 text-rose-900 text-xs rounded-full flex items-center justify-center font-bold">
                {unreadCount}
              </span>
            )}
            <span className="text-xs font-medium">Alerts</span>
          </button>
        </div>
      </nav>
    </div>
  );
}
